import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import RadioField from 'components/application/RadioField';

import TeamsAccessExplanationNotification from 'components/settings/users/TeamsAccessExplanationNotification';
import AccessControlTable from 'components/settings/users/userForm/components/AccessControlTable';
import { useUserFormContext } from 'components/settings/users/userForm/contexts/UserFormContext';
import UserPermissionsAndDivisionAccessInfoCard from 'components/settings/users/UserPermissionsAndDivisionAccessInfoCard';

export default function AccessManagerSection({
  assignablePermissions,
  currentUser,
  fullUserAllowanceConsumptionCount,
  fullUserAllowanceCount,
  isAtFullUserCapacity,
  isAtPersonnelReporteeManagerCapacity,
  isEditingSelf,
  isFullUserAllowanceConsumptionDisabled,
  isPersonnelReporteeManagerAllowanceConsumptionDisabled,
  onAccessTypeSelection,
  onPermissionChange,
  onSelectAllChange,
  personnelReporteeManagerAllowanceConsumptionCount,
  personnelReporteeManagerAllowanceCount,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'settings.users.userForm.components.AccessManagerSection',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const { teamPolicy } = useUserFormContext();

  return (
    <>
      <h3 className='tw-my-6 tw-text-l tw-font-semibold tw-tracking-auto tw-text-grey-900'>
        {i18nT('title')}
      </h3>
      <div className='tw-mb-6'>
        <UserPermissionsAndDivisionAccessInfoCard />
      </div>
      {currentUser.accessType === 'primary' ?
        <>
          <p>{i18nTCommon('settings.accountOwner')}</p>
          <p>{i18nTCommon('settings.changeAccountOwner')}</p>
        </>
      : <>
          <RadioField
            checked={currentUser.accessType === 'administrator'}
            label={
              <div className='tw-ml-1'>
                <div className='tw-mb-1 tw-font-medium'>
                  {i18nT('accessType.administrator.label')}
                </div>
                <div className='tw-text-s tw-font-normal tw-tracking-wide'>
                  {i18nT('accessType.administrator.description')}
                </div>
              </div>
            }
            name='access_type'
            onChange={() => onAccessTypeSelection('administrator')}
          />
          <div className='tw-h-px' />
          <RadioField
            checked={currentUser.accessType === 'regular'}
            disabled={isEditingSelf}
            label={
              <div className='tw-ml-1'>
                <div className='tw-mb-1 tw-font-medium'>
                  {i18nTCommon('shared.user')}
                </div>
                <div className='tw-text-s tw-font-normal tw-tracking-wide'>
                  {i18nT('accessType.regular.description')}
                </div>
              </div>
            }
            name='access_type'
            onChange={() => onAccessTypeSelection('regular')}
            tooltip={i18nTCommon(
              'settings.cannotDowngradeOwnAccessAndPermissions',
            )}
          />
          {teamPolicy.canIndex && <TeamsAccessExplanationNotification />}
          {currentUser.accessType === 'regular' && (
            <AccessControlTable
              assignablePermissions={assignablePermissions}
              currentUser={currentUser}
              fullUserAllowanceConsumptionCount={
                fullUserAllowanceConsumptionCount
              }
              fullUserAllowanceCount={fullUserAllowanceCount}
              isAtFullUserCapacity={isAtFullUserCapacity}
              isAtPersonnelReporteeManagerCapacity={
                isAtPersonnelReporteeManagerCapacity
              }
              isFullUserAllowanceConsumptionDisabled={
                isFullUserAllowanceConsumptionDisabled
              }
              isPersonnelReporteeManagerAllowanceConsumptionDisabled={
                isPersonnelReporteeManagerAllowanceConsumptionDisabled
              }
              onPermissionChange={onPermissionChange}
              onSelectAllChange={onSelectAllChange}
              personnelReporteeManagerAllowanceConsumptionCount={
                personnelReporteeManagerAllowanceConsumptionCount
              }
              personnelReporteeManagerAllowanceCount={
                personnelReporteeManagerAllowanceCount
              }
            />
          )}
        </>
      }
    </>
  );
}

AccessManagerSection.propTypes = {
  fullUserAllowanceCount: PropTypes.number,
  assignablePermissions: PropTypes.array.isRequired,
  fullUserAllowanceConsumptionCount: PropTypes.number,
  personnelReporteeManagerAllowanceConsumptionCount: PropTypes.number,
  personnelReporteeManagerAllowanceCount: PropTypes.number,
  currentUser: PropTypes.object.isRequired,
  isAtFullUserCapacity: PropTypes.bool,
  isAtPersonnelReporteeManagerCapacity: PropTypes.bool,
  isEditingSelf: PropTypes.bool.isRequired,
  isFullUserAllowanceConsumptionDisabled: PropTypes.bool,
  isPersonnelReporteeManagerAllowanceConsumptionDisabled: PropTypes.bool,
  onAccessTypeSelection: PropTypes.func.isRequired,
  onPermissionChange: PropTypes.func.isRequired,
  onSelectAllChange: PropTypes.func.isRequired,
};
